/**
 * The two reads every membership surface on the donor web starts from (GH-864e).
 *
 * Both follow one rule: **a 404 hides everything**. `FEATURE_MEMBERSHIP` defaults off, and while it
 * is off the API answers every membership route 404 — so a donor on a deployment without it sees
 * exactly the site they saw before GH-864, with no empty card, no dead button and no error. A read
 * that fails for any other reason hides too: membership is never worth a red box on a page whose
 * job is a gift.
 */
import { useEffect, useState } from "react";
import type { DonorMembershipResponse, MembershipPulseResponse } from "@givehere/contracts";
import { fetchMembershipPulse } from "./api.js";
import { fetchDonorMembership } from "./donor-session.js";

/**
 * `undefined` while unknown, `null` when the gate is off (or unreadable), else the pulse — whose
 * `waitingDisplay` is itself null below the server's threshold.
 */
export function useMembershipPulse(enabled = true): MembershipPulseResponse | null | undefined {
  const [pulse, setPulse] = useState<MembershipPulseResponse | null | undefined>(undefined);
  useEffect(() => {
    if (!enabled) return;
    let live = true;
    fetchMembershipPulse().then(
      (value) => live && setPulse(value),
      () => live && setPulse(null),
    );
    return () => {
      live = false;
    };
  }, [enabled]);
  return pulse;
}

export type MembershipState = {
  /** `undefined` while unknown, `null` when the gate is off or there is no session. */
  readonly membership: DonorMembershipResponse | null | undefined;
  /** Replace with the server's newer answer after a write. */
  readonly setMembership: (next: DonorMembershipResponse) => void;
};

/** The signed-in donor's own membership; nothing is fetched without a token. */
export function useDonorMembership(token: string | null): MembershipState {
  const [membership, setMembership] = useState<DonorMembershipResponse | null | undefined>(
    token ? undefined : null,
  );
  useEffect(() => {
    if (!token) {
      setMembership(null);
      return;
    }
    let live = true;
    fetchDonorMembership(token).then(
      (value) => live && setMembership(value),
      () => live && setMembership(null),
    );
    return () => {
      live = false;
    };
  }, [token]);
  return { membership, setMembership };
}
